import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ShoppingCart, Minus, Plus, X, Trash2, Heart } from 'lucide-react';
import CheckoutFlow from './CheckoutFlow';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getHeartPizzaAllowance, getHeartPizzaDefaultFlavours, isRegularPizza } from '../utils/orderingTime';
import HeartFlavourPicker from './HeartFlavourPicker';
import API_BASE_URL from '../config/api';

const CartModalWithCheckout = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, onPlaceOrder }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showCheckout, setShowCheckout] = useState(false);
  const modalContentRef = useRef(null);
  // Omani Women's Day free heart-shaped pizzas: null follows the allowance, a number is the customer's pick
  const [heartPizzaChoice, setHeartPizzaChoice] = useState(null);
  const heartPizzaAllowance = getHeartPizzaAllowance(cartItems);
  const heartPizzaCount = heartPizzaChoice === null
    ? heartPizzaAllowance
    : Math.min(heartPizzaChoice, heartPizzaAllowance);
  // Flavour per heart pizza: defaults to the pizza that earned it; overrides holds the customer's changes by position
  const [heartFlavourOverrides, setHeartFlavourOverrides] = useState({});
  const heartFlavours = getHeartPizzaDefaultFlavours(cartItems)
    .slice(0, heartPizzaCount)
    .map((defaultId, index) => heartFlavourOverrides[index] || defaultId);
  const setHeartFlavour = (index, id) => setHeartFlavourOverrides(prev => ({ ...prev, [index]: id }));
  // Pizzas the customer can pick as a heart flavour, loaded once while the offer runs
  const [pizzaFlavours, setPizzaFlavours] = useState([]);
  useEffect(() => {
    if (heartPizzaAllowance === 0 || pizzaFlavours.length > 0) return;
    axios.get(`${API_BASE_URL}/api/menu`)
      .then(res => setPizzaFlavours((res.data.menu_items || []).filter(item => item.available && isRegularPizza(item))))
      .catch(err => console.error('Error loading heart pizza flavours:', err));
  }, [heartPizzaAllowance, pizzaFlavours.length]);

  // Forget the picks once nothing qualifies (e.g. after an order is placed), so the next order starts fresh
  useEffect(() => {
    if (heartPizzaAllowance === 0) {
      setHeartPizzaChoice(null);
      setHeartFlavourOverrides({});
    }
  }, [heartPizzaAllowance]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div ref={modalContentRef} className="bg-white rounded-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto relative shadow-2xl border border-gray-100">
        {/* Global Exit Button - Always visible */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[60] bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full h-10 w-10 flex items-center justify-center shadow-md transition-all active:scale-95 group border border-gray-200"
          aria-label="Close modal"
        >
          <X className="h-6 w-6 group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <div className="p-4 sm:p-8">
          {!showCheckout || cartItems.length === 0 ? (
            // Cart View
            <div className="pt-2">
              <div className="flex items-center space-x-3 mb-8 border-b border-gray-100 pb-4">
                <div className="bg-red-50 p-2 rounded-xl">
                  <ShoppingCart className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight">{t('cart.title')}</h2>
              </div>

              {cartItems.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="bg-gray-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ShoppingCart className="h-12 w-12 text-gray-300" />
                  </div>
                  <p className="text-xl font-bold text-gray-800 mb-2">{t('cart.feelingHungry')}</p>
                  <p className="text-gray-500 mb-8">{t('cart.emptySubtitle')}</p>
                  <Button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-8 py-6 text-lg font-bold shadow-lg shadow-red-500/30 transition-all active:scale-95">
                    {t('cart.startOrdering')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                    {cartItems.map(item => (
                      <div key={item.cartId || item.id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-3 sm:gap-x-6 p-4 sm:p-5 border border-gray-100 rounded-2xl bg-gray-50/50 hover:bg-white hover:shadow-xl hover:shadow-gray-100 transition-all mb-4 group ring-1 ring-transparent hover:ring-red-100">
                        <div className="relative flex-shrink-0">
                          <img src={item.image} alt={item.name} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300" />
                          <div className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-sm">
                            {item.quantity}×
                          </div>
                        </div>

                        <div className="flex-1 text-start min-w-0">
                          <h3 className="font-black text-gray-900 text-base sm:text-lg break-words mb-1 leading-tight">{lang === 'ar' && item.name_ar ? item.name_ar : item.name}</h3>
                          <div className="flex items-center justify-start gap-2 mb-1">
                            {item.selectedSize && (
                              <span className="bg-red-50 text-red-700 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md">
                                {item.selectedSize}
                              </span>
                            )}
                            <span className="text-red-600 font-bold text-sm"><span dir="ltr">OMR {item.price.toFixed(3)}</span></span>
                          </div>
                          {item.selections && item.selections.length > 0 && (
                            <div className="flex flex-wrap justify-start gap-1">
                              {item.selections.map((sel, idx) => (
                                <span key={idx} className="text-[10px] bg-white border border-gray-200 text-gray-500 px-2 py-0.5 rounded-md">
                                  {sel.items.join(', ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between w-full sm:w-auto sm:gap-4">
                          <div className="flex items-center bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
                            <Button
                              onClick={() => onUpdateQuantity(item.cartId, item.quantity - 1)}
                              variant="ghost"
                              size="sm"
                              className="h-11 w-11 p-0 rounded-lg hover:bg-red-50 hover:text-red-600"
                            >
                              <Minus className="h-5 w-5" />
                            </Button>
                            <span className="w-10 text-center font-black text-gray-900 text-lg">{item.quantity}</span>
                            <Button
                              onClick={() => onUpdateQuantity(item.cartId, item.quantity + 1)}
                              variant="ghost"
                              size="sm"
                              className="h-11 w-11 p-0 rounded-lg hover:bg-green-50 hover:text-green-600"
                            >
                              <Plus className="h-5 w-5" />
                            </Button>
                          </div>

                          <Button
                            onClick={() => onRemoveItem(item.cartId)}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg h-10 w-10 p-0"
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {heartPizzaAllowance > 0 && (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 sm:gap-x-6 p-4 sm:p-5 border-2 border-red-200 rounded-2xl bg-red-50/60 mb-4">
                        <div className="relative flex-shrink-0">
                          <img src="/assets/logo/heart_pizza.jpeg" alt={t('womensDay.freeItem')} className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white p-1 rounded-xl shadow-md" />
                          <div className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-sm">
                            {heartPizzaCount}×
                          </div>
                        </div>

                        <div className="flex-1 text-start min-w-0">
                          <h3 className="font-black text-red-700 text-base sm:text-lg break-words mb-1 leading-tight">
                            🎁 <Heart className="inline h-4 w-4 fill-red-600 text-red-600 align-[-2px]" /> {t('womensDay.freeItem')}
                          </h3>
                          <div className="text-gray-500 text-sm">{t('womensDay.chooseUpTo', { max: heartPizzaAllowance })}</div>
                        </div>

                        <div className="flex items-center justify-between w-full sm:w-auto sm:gap-4">
                        <div className="flex items-center bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
                          <Button
                            onClick={() => setHeartPizzaChoice(Math.max(0, heartPizzaCount - 1))}
                            disabled={heartPizzaCount <= 0}
                            aria-label={t('womensDay.removeOne')}
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 rounded-lg hover:bg-red-50 hover:text-red-600"
                          >
                            <Minus className="h-5 w-5" />
                          </Button>
                          <span className="w-10 text-center font-black text-gray-900 text-lg">{heartPizzaCount}</span>
                          <Button
                            onClick={() => setHeartPizzaChoice(Math.min(heartPizzaAllowance, heartPizzaCount + 1))}
                            disabled={heartPizzaCount >= heartPizzaAllowance}
                            aria-label={t('womensDay.addOne')}
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 rounded-lg hover:bg-green-50 hover:text-green-600"
                          >
                            <Plus className="h-5 w-5" />
                          </Button>
                        </div>
                        <span className="bg-green-100 text-green-700 text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-lg">
                          {t('womensDay.free')}
                        </span>
                        </div>

                        <HeartFlavourPicker flavours={heartFlavours} options={pizzaFlavours} onChange={setHeartFlavour} />
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-inner">
                    <div className="flex justify-between items-center mb-6">
                      <div className="flex flex-col">
                        <span className="text-gray-500 text-sm font-bold uppercase tracking-widest">{t('cart.estimatedTotal')}</span>
                        <span className="text-xs text-gray-400">{t('cart.taxNote')}</span>
                      </div>
                      <div className="text-right">
                        <span dir="ltr" className="block text-3xl font-black text-gray-900 tracking-tighter">
                          OMR {cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(3)}
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={() => {
                        setShowCheckout(true);
                        setTimeout(() => {
                          if (modalContentRef.current) {
                            modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }, 100);
                      }}
                      className="w-full bg-red-600 hover:bg-red-700 text-white rounded-2xl py-8 text-xl font-black shadow-xl shadow-red-500/30 transition-all hover:scale-[1.02] active:scale-95 group overflow-hidden relative"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        {t('cart.proceedToCheckout')}
                        <Plus className="ms-2 h-5 w-5 group-hover:rotate-90 transition-transform" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    </Button>

                    <button
                      onClick={onClose}
                      className="w-full mt-4 text-gray-500 hover:text-gray-800 font-bold text-sm transition-colors"
                    >
                      {t('cart.continueShopping')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Checkout Flow
            <div className="relative">
              <CheckoutFlow
                cart={cartItems}
                onPlaceOrder={onPlaceOrder}
                onBackToMenu={() => setShowCheckout(false)}
                scrollContainerRef={modalContentRef}
                heartPizzaChoice={heartPizzaChoice}
                onHeartPizzaChoiceChange={setHeartPizzaChoice}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveItem={onRemoveItem}
                heartFlavours={heartFlavours}
                pizzaFlavours={pizzaFlavours}
                onHeartFlavourChange={setHeartFlavour}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartModalWithCheckout; 